import { useState } from "react";

import { EyeIcon, EyeSlashIcon, PencilIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { ApplicationSettingsList } from "./ApplicationSettingsList";
import useApplicationSettingsMutations from "../../data/ApplicationSettings/useApplicationSettingsMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

interface CustomerApplicationSettingsModalProps extends ModalControlProps {
  applicationRegistrationId: string;
}

export function CustomerApplicationSettingsModal({
  open,
  setOpen,
  applicationRegistrationId,
}: CustomerApplicationSettingsModalProps) {
  const { deleteHandlers } = useDefaultCRUDHandlers("Application setting");
  const { remove } = useApplicationSettingsMutations({
    applicationRegistrationId,
  });
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleClose = () => {
    setOpen(false);
    editing &&
      setTimeout(() => {
        setEditing(false);
      }, 250);
  };

  const handleEditingComplete = (success: boolean) => {
    setEditing(false);
  };

  const handleDeleteSetting = (settingId: string) => {
    remove.mutate(settingId, { ...deleteHandlers });
  };

  return (
    <Modal open={open} setOpen={handleClose} width="!w-2/3">
      <div>
        <div className="pl-4 pr-3.5 flex justify-between items-center border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-4 text-gray-900">
            Application Settings
          </h2>
          <div className="flex flex-row items-center space-x-4">
            {!editing && (
              <Button
                onClick={() => setEditing(true)}
                size="sm"
                className="-mt-4 px-3 flex items-center space-x-2"
              >
                <PencilIcon className="h-5 w-5 -ml-1" />
                <p>Edit Config</p>
              </Button>
            )}
            <Button
              onClick={() => setShowAll((sa) => !sa)}
              size="sm"
              className="-mt-4 px-3 flex items-center space-x-2"
            >
              {!showAll ? (
                <EyeIcon className="h-5 w-5 -ml-1" />
              ) : (
                <EyeSlashIcon className="h-5 w-5 -ml-1" />
              )}
              <p>{!showAll ? "Show All" : "Hide All"} Config</p>
            </Button>
          </div>
        </div>
        <ApplicationSettingsList
          ids={{ applicationRegistrationId }}
          editing={editing}
          onComplete={handleEditingComplete}
          onDelete={handleDeleteSetting}
          showAll={showAll}
        />
      </div>
    </Modal>
  );
}
